// src/lib/utils/colors/transforms.ts

import type { RGB, HSL, HSV, HWB, CMYK, XYZ, LAB, OKLAB, LCH, OKLCH, HCG, Apple, Gray, HEX, Keyword, ANSI16, ANSI256, ColorSpace, ColorValue, RGBA, HSLA, HSVA, HWBA } from '~/lib/utils/colors/types'
import {
  D65_X,
  D65_Y,
  D65_Z,
  LAB_FT,
  CSS_KEYWORDS,
  REVERSE_KEYWORDS
} from '~/lib/utils/colors/types'

/**
 * Clamps a number between min and max values
 * @param {number} value - The value to clamp
 * @param {number} min - The minimum value
 * @param {number} max - The maximum value
 * @returns {number} The clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(min, value), max)
}

/**
 * Rounds a number to specified decimal places
 * @param {number} value - The value to round
 * @param {number} places - Number of decimal places
 * @returns {number} The rounded value
 */
export function roundTo(value: number, places: number): number {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

/**
 * Normalizes hue to 0-360 range
 * @param {number} hue - Hue value in degrees
 * @returns {number} Normalized hue value
 */
export const normalizeHue = (hue: number): number => {
    hue = hue % 360
    return hue < 0 ? hue + 360 : hue
  }

/**
 * sRGB non-linear transform function
 * @param {number} c - Linear RGB component
 * @returns {number} Non-linear sRGB component
 */
function srgbNonlinearTransform(c: number): number {
  const cc = c > 0.0031308
    ? ((1.055 * (c ** (1 / 2.4))) - 0.055)
    : c * 12.92
  return clamp(cc, 0, 1)
}

/**
 * Inverse sRGB non-linear transform function
 * @param {number} c - Non-linear sRGB component
 * @returns {number} Linear RGB component
 */
function srgbNonlinearTransformInv(c: number): number {
  return c > 0.04045 ? (((c + 0.055) / 1.055) ** 2.4) : (c / 12.92)
}

/**
 * Calculates comparative distance between two RGB colors
 * @param {RGB} x - First RGB color
 * @param {RGB} y - Second RGB color
 * @returns {number} Squared Euclidean distance
 */
function comparativeDistance(x: RGB, y: RGB): number {
  return ((x[0] - y[0]) ** 2) + ((x[1] - y[1]) ** 2) + ((x[2] - y[2]) ** 2)
}

/**
 * Converts RGB to HSL color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {HSL} HSL color values [0-360, 0-100, 0-100]
 */
export function rgbToHsl(rgb: RGB): HSL {
  const r = rgb[0] / 255
  const g = rgb[1] / 255
  const b = rgb[2] / 255
  const min = Math.min(r, g, b)
  const max = Math.max(r, g, b)
  const delta = max - min
  let h: number
  let s: number

  switch (max) {
    case min:
      h = 0
      break
    case r:
      h = (g - b) / delta
      break
    case g:
      h = 2 + (b - r) / delta
      break
    case b:
      h = 4 + (r - g) / delta
      break
    default:
      h = 0
  }

  h = Math.min(h * 60, 360)
  if (h < 0) h += 360

  const l = (min + max) / 2

  if (max === min) {
    s = 0
  } else if (l <= 0.5) {
    s = delta / (max + min)
  } else {
    s = delta / (2 - max - min)
  }

  return [h, s * 100, l * 100]
}

/**
 * Converts HSL to RGB color space
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function hslToRgb(hsl: HSL): RGB {
  const h = hsl[0] / 360
  const s = hsl[1] / 100
  const l = hsl[2] / 100
  let value: number

  if (s === 0) {
    value = l * 255
    return [value, value, value]
  }

  const t2 = l < 0.5 ? l * (1 + s) : l + s - l * s
  const t1 = 2 * l - t2

  const rgb: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < 3; i++) {
    let t3 = h + 1 / 3 * -(i - 1)
    if (t3 < 0) t3++
    if (t3 > 1) t3--

    if (6 * t3 < 1) {
      value = t1 + (t2 - t1) * 6 * t3
    } else if (2 * t3 < 1) {
      value = t2
    } else if (3 * t3 < 2) {
      value = t1 + (t2 - t1) * (2 / 3 - t3) * 6
    } else {
      value = t1
    }

    rgb[i] = value * 255
  }

  return rgb
}

/**
 * Converts RGB to HSV color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {HSV} HSV color values [0-360, 0-100, 0-100]
 */
export function rgbToHsv(rgb: RGB): HSV {
  const r = rgb[0] / 255
  const g = rgb[1] / 255
  const b = rgb[2] / 255
  const v = Math.max(r, g, b)
  const diff = v - Math.min(r, g, b)
  let h: number
  let s: number

  if (diff === 0) {
    h = 0
    s = 0
  } else {
    s = diff / v
    const diffc = (c: number): number => (v - c) / 6 / diff + 1 / 2
    const rdif = diffc(r)
    const gdif = diffc(g)
    const bdif = diffc(b)

    switch (v) {
      case r:
        h = bdif - gdif
        break
      case g:
        h = (1 / 3) + rdif - bdif
        break
      case b:
        h = (2 / 3) + gdif - rdif
        break
      default:
        h = 0
    }

    if (h < 0) {
      h += 1
    } else if (h > 1) {
      h -= 1
    }
  }

  return [h * 360, s * 100, v * 100]
}

/**
 * Converts HSV to RGB color space
 * @param {HSV} hsv - HSV color values [0-360, 0-100, 0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function hsvToRgb(hsv: HSV): RGB {
  const h = hsv[0] / 60
  const s = hsv[1] / 100
  let v = hsv[2] / 100
  const hi = Math.floor(h) % 6

  const f = h - Math.floor(h)
  const p = 255 * v * (1 - s)
  const q = 255 * v * (1 - (s * f))
  const t = 255 * v * (1 - (s * (1 - f)))
  v *= 255

  switch (hi) {
    case 0:
      return [v, t, p]
    case 1:
      return [q, v, p]
    case 2:
      return [p, v, t]
    case 3:
      return [p, q, v]
    case 4:
      return [t, p, v]
    case 5:
      return [v, p, q]
    default:
      return [0, 0, 0]
  }
}

/**
 * Converts HSL to HSV color space
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @returns {HSV} HSV color values [0-360, 0-100, 0-100]
 */
export function hslToHsv(hsl: HSL): HSV {
  const h = hsl[0]
  let s = hsl[1] / 100
  let l = hsl[2] / 100
  let smin = s
  const lmin = Math.max(l, 0.01)

  l *= 2
  s *= (l <= 1) ? l : 2 - l
  smin *= lmin <= 1 ? lmin : 2 - lmin
  const v = (l + s) / 2
  const sv = l === 0 ? (2 * smin) / (lmin + smin) : (2 * s) / (l + s)

  return [h, sv * 100, v * 100]
}

/**
 * Converts HSV to HSL color space
 * @param {HSV} hsv - HSV color values [0-360, 0-100, 0-100]
 * @returns {HSL} HSL color values [0-360, 0-100, 0-100]
 */
export function hsvToHsl(hsv: HSV): HSL {
  const h = hsv[0]
  const s = hsv[1] / 100
  const v = hsv[2] / 100
  const vmin = Math.max(v, 0.01)
  let sl: number
  let l: number

  l = (2 - s) * v
  const lmin = (2 - s) * vmin
  sl = s * vmin
  sl /= (lmin <= 1) ? lmin : 2 - lmin
  sl = sl || 0
  l /= 2

  return [h, sl * 100, l * 100]
}

/**
 * Converts RGB to HWB color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {HWB} HWB color values [0-360, 0-100, 0-100]
 */
export function rgbToHwb(rgb: RGB): HWB {
  const r = rgb[0]
  const g = rgb[1]
  const b = rgb[2]
  const h = rgbToHsl(rgb)[0]
  const w = 1 / 255 * Math.min(r, Math.min(g, b))
  const bl = 1 - 1 / 255 * Math.max(r, Math.max(g, b))

  return [h, w * 100, bl * 100]
}

/**
 * Converts HWB to RGB color space
 * @param {HWB} hwb - HWB color values [0-360, 0-100, 0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function hwbToRgb(hwb: HWB): RGB {
  const h = hwb[0] / 360
  let wh = hwb[1] / 100
  let bl = hwb[2] / 100
  const ratio = wh + bl

  // Wh + bl cant be > 1
  if (ratio > 1) {
    wh /= ratio
    bl /= ratio
  }

  const i = Math.floor(6 * h)
  const v = 1 - bl
  let f = 6 * h - i

  if ((i & 0x01) !== 0) {
    f = 1 - f
  }

  const n = wh + f * (v - wh) // Linear interpolation

  let r: number
  let g: number
  let b: number

  switch (i) {
    default:
    case 6:
    case 0:
      r = v
      g = n
      b = wh
      break
    case 1:
      r = n
      g = v
      b = wh
      break
    case 2:
      r = wh
      g = v
      b = n
      break
    case 3:
      r = wh
      g = n
      b = v
      break
    case 4:
      r = n
      g = wh
      b = v
      break
    case 5:
      r = v
      g = wh
      b = n
      break
  }

  return [r * 255, g * 255, b * 255]
}

/**
 * Converts RGB to CMYK color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {CMYK} CMYK color values [0-100, 0-100, 0-100, 0-100]
 */
export function rgbToCmyk(rgb: RGB): CMYK {
  const r = rgb[0] / 255
  const g = rgb[1] / 255
  const b = rgb[2] / 255

  const k = Math.min(1 - r, 1 - g, 1 - b)
  const c = (1 - r - k) / (1 - k) || 0
  const m = (1 - g - k) / (1 - k) || 0
  const y = (1 - b - k) / (1 - k) || 0

  return [c * 100, m * 100, y * 100, k * 100]
}

/**
 * Converts CMYK to RGB color space
 * @param {CMYK} cmyk - CMYK color values [0-100, 0-100, 0-100, 0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function cmykToRgb(cmyk: CMYK): RGB {
  const c = cmyk[0] / 100
  const m = cmyk[1] / 100
  const y = cmyk[2] / 100
  const k = cmyk[3] / 100

  const r = 1 - Math.min(1, c * (1 - k) + k)
  const g = 1 - Math.min(1, m * (1 - k) + k)
  const b = 1 - Math.min(1, y * (1 - k) + k)

  return [r * 255, g * 255, b * 255]
}

/**
 * Converts RGB to XYZ color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {XYZ} XYZ color values
 */
export function rgbToXyz(rgb: RGB): XYZ {
  // Assume sRGB
  const r = srgbNonlinearTransformInv(rgb[0] / 255)
  const g = srgbNonlinearTransformInv(rgb[1] / 255)
  const b = srgbNonlinearTransformInv(rgb[2] / 255)

  const x = (r * 0.4124564) + (g * 0.3575761) + (b * 0.1804375)
  const y = (r * 0.2126729) + (g * 0.7151522) + (b * 0.072175)
  const z = (r * 0.0193339) + (g * 0.119192) + (b * 0.9503041)

  return [x * 100, y * 100, z * 100]
}

/**
 * Converts XYZ to RGB color space
 * @param {XYZ} xyz - XYZ color values
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function xyzToRgb(xyz: XYZ): RGB {
  const x = xyz[0] / 100
  const y = xyz[1] / 100
  const z = xyz[2] / 100

  let r = (x * 3.2404542) + (y * -1.5371385) + (z * -0.4985314)
  let g = (x * -0.969266) + (y * 1.8760108) + (z * 0.041556)
  let b = (x * 0.0556434) + (y * -0.2040259) + (z * 1.0572252)

  // Assume sRGB
  r = srgbNonlinearTransform(r)
  g = srgbNonlinearTransform(g)
  b = srgbNonlinearTransform(b)

  return [r * 255, g * 255, b * 255]
}

/**
 * Converts RGB to LAB color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {LAB} LAB color values
 */
export function rgbToLab(rgb: RGB): LAB {
  const xyz = rgbToXyz(rgb)
  let x = xyz[0]
  let y = xyz[1]
  let z = xyz[2]

  x /= D65_X
  y /= D65_Y
  z /= D65_Z

  x = x > LAB_FT ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116)
  y = y > LAB_FT ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116)
  z = z > LAB_FT ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116)

  const l = (116 * y) - 16
  const a = 500 * (x - y)
  const b = 200 * (y - z)

  return [l, a, b]
}

/**
 * Converts XYZ to LAB color space
 * @param {XYZ} xyz - XYZ color values
 * @returns {LAB} LAB color values
 */
export function xyzToLab(xyz: XYZ): LAB {
  let x = xyz[0]
  let y = xyz[1]
  let z = xyz[2]

  x /= D65_X
  y /= D65_Y
  z /= D65_Z

  x = x > LAB_FT ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116)
  y = y > LAB_FT ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116)
  z = z > LAB_FT ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116)

  const l = (116 * y) - 16
  const a = 500 * (x - y)
  const b = 200 * (y - z)

  return [l, a, b]
}

/**
 * Converts LAB to XYZ color space
 * @param {LAB} lab - LAB color values
 * @returns {XYZ} XYZ color values
 */
export function labToXyz(lab: LAB): XYZ {
  const l = lab[0]
  const a = lab[1]
  const b = lab[2]
  let x: number, y: number, z: number

  y = (l + 16) / 116
  x = a / 500 + y
  z = y - b / 200

  const y2 = y ** 3
  const x2 = x ** 3
  const z2 = z ** 3

  y = y2 > LAB_FT ? y2 : (y - 16 / 116) / 7.787
  x = x2 > LAB_FT ? x2 : (x - 16 / 116) / 7.787
  z = z2 > LAB_FT ? z2 : (z - 16 / 116) / 7.787

  // Illuminant D65 XYZ Tristrimulus Values
  x *= D65_X
  y *= D65_Y
  z *= D65_Z

  return [x, y, z]
}

/**
 * Converts RGB to OKLAB color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {OKLAB} OKLAB color values
 */
export function rgbToOklab(rgb: RGB): OKLAB {
  // Assume sRGB
  const r = srgbNonlinearTransformInv(rgb[0] / 255)
  const g = srgbNonlinearTransformInv(rgb[1] / 255)
  const b = srgbNonlinearTransformInv(rgb[2] / 255)

  const lp = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const mp = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const sp = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const l = 0.2104542553 * lp + 0.793617785 * mp - 0.0040720468 * sp
  const a = 1.9779984951 * lp - 2.428592205 * mp + 0.4505937099 * sp
  const bl = 0.0259040371 * lp + 0.7827717662 * mp - 0.808675766 * sp

  return [l * 100, a * 100, bl * 100]
}

/**
 * Converts OKLAB to RGB color space
 * @param {OKLAB} oklab - OKLAB color values
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function oklabToRgb(oklab: OKLAB): RGB {
  const ll = oklab[0] / 100
  const aa = oklab[1] / 100
  const bb = oklab[2] / 100

  const l = (ll + 0.3963377774 * aa + 0.2158037573 * bb) ** 3
  const m = (ll - 0.1055613458 * aa - 0.0638541728 * bb) ** 3
  const s = (ll - 0.0894841775 * aa - 1.291485548 * bb) ** 3

  // Assume sRGB
  const r = srgbNonlinearTransform(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)
  const g = srgbNonlinearTransform(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s)
  const b = srgbNonlinearTransform(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)

  return [r * 255, g * 255, b * 255]
}

/**
 * Converts LAB to LCH color space
 * @param {LAB} lab - LAB color values
 * @returns {LCH} LCH color values
 */
export function labToLch(lab: LAB): LCH {
  const l = lab[0]
  const a = lab[1]
  const b = lab[2]

  const hr = Math.atan2(b, a)
  let h = hr * 360 / 2 / Math.PI

  if (h < 0) {
    h += 360
  }

  const c = Math.sqrt(a * a + b * b)

  return [l, c, h]
}

/**
 * Converts LCH to LAB color space
 * @param {LCH} lch - LCH color values
 * @returns {LAB} LAB color values
 */
export function lchToLab(lch: LCH): LAB {
  const l = lch[0]
  const c = lch[1]
  const h = lch[2]

  const hr = h / 360 * 2 * Math.PI
  const a = c * Math.cos(hr)
  const b = c * Math.sin(hr)

  return [l, a, b]
}

/**
 * Converts OKLAB to OKLCH color space
 * @param {OKLAB} oklab - OKLAB color values
 * @returns {OKLCH} OKLCH color values
 */
export function oklabToOklch(oklab: OKLAB): OKLCH {
  return labToLch(oklab) // Same conversion as LAB to LCH
}

/**
 * Converts OKLCH to OKLAB color space
 * @param {OKLCH} oklch - OKLCH color values
 * @returns {OKLAB} OKLAB color values
 */
export function oklchToOklab(oklch: OKLCH): OKLAB {
  return lchToLab(oklch) // Same conversion as LCH to LAB
}

/**
 * Converts RGB to HCG color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {HCG} HCG color values [0-360, 0-100, 0-100]
 */
export function rgbToHcg(rgb: RGB): HCG {
  const r = rgb[0] / 255
  const g = rgb[1] / 255
  const b = rgb[2] / 255
  const max = Math.max(Math.max(r, g), b)
  const min = Math.min(Math.min(r, g), b)
  const chroma = (max - min)
  let hue: number

  const grayscale = chroma < 1 ? min / (1 - chroma) : 0

  if (chroma <= 0) {
    hue = 0
  } else if (max === r) {
    hue = ((g - b) / chroma) % 6
  } else if (max === g) {
    hue = 2 + (b - r) / chroma
  } else {
    hue = 4 + (r - g) / chroma
  }

  hue /= 6
  hue %= 1

  return [hue * 360, chroma * 100, grayscale * 100]
}

/**
 * Converts HCG to RGB color space
 * @param {HCG} hcg - HCG color values [0-360, 0-100, 0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function hcgToRgb(hcg: HCG): RGB {
  const h = hcg[0] / 360
  const c = hcg[1] / 100
  const g = hcg[2] / 100

  if (c === 0) {
    return [g * 255, g * 255, g * 255]
  }

  const pure = [0, 0, 0]
  const hi = (h % 1) * 6
  const v = hi % 1
  const w = 1 - v

  switch (Math.floor(hi)) {
    case 0:
      pure[0] = 1
      pure[1] = v
      pure[2] = 0
      break
    case 1:
      pure[0] = w
      pure[1] = 1
      pure[2] = 0
      break
    case 2:
      pure[0] = 0
      pure[1] = 1
      pure[2] = v
      break
    case 3:
      pure[0] = 0
      pure[1] = w
      pure[2] = 1
      break
    case 4:
      pure[0] = v
      pure[1] = 0
      pure[2] = 1
      break
    default:
      pure[0] = 1
      pure[1] = 0
      pure[2] = w
  }

  const mg = (1 - c) * g

  return [
    (c * pure[0] + mg) * 255,
    (c * pure[1] + mg) * 255,
    (c * pure[2] + mg) * 255,
  ]
}

/**
 * Converts RGB to Apple RGB color space
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {Apple} Apple RGB color values [0-65535, 0-65535, 0-65535]
 */
export function rgbToApple(rgb: RGB): Apple {
  return [(rgb[0] / 255) * 65535, (rgb[1] / 255) * 65535, (rgb[2] / 255) * 65535]
}

/**
 * Converts Apple RGB to RGB color space
 * @param {Apple} apple - Apple RGB color values [0-65535, 0-65535, 0-65535]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function appleToRgb(apple: Apple): RGB {
  return [(apple[0] / 65535) * 255, (apple[1] / 65535) * 255, (apple[2] / 65535) * 255]
}

/**
 * Converts RGB to grayscale
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {Gray} Grayscale color value [0-100]
 */
export function rgbToGrayscale(rgb: RGB): Gray {
  const value = (rgb[0] + rgb[1] + rgb[2]) / 3
  return [value / 255 * 100]
}

/**
 * Converts grayscale to RGB
 * @param {Gray} gray - Grayscale color value [0-100]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function grayscaleToRgb(gray: Gray): RGB {
  const value = gray[0] / 100 * 255
  return [value, value, value]
}

/**
 * Converts RGB to HEX string
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {HEX} HEX color string
 */
export function rgbToHex(rgb: RGB): HEX {
  const integer = ((Math.round(rgb[0]) & 0xFF) << 16)
    + ((Math.round(rgb[1]) & 0xFF) << 8)
    + (Math.round(rgb[2]) & 0xFF)

  const string = integer.toString(16).toUpperCase()
  return ('#' + '000000'.slice(string.length) + string)
}

/**
 * Converts HEX string to RGB
 * @param {HEX} hex - HEX color string
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function hexToRgb(hex: HEX): RGB {
  const match = /[a-f\d]{6}|[a-f\d]{3}/i.exec(hex.toString().replace('#', ''))
  if (!match) {
    return [0, 0, 0]
  }

  let colorString = match[0]

  if (match[0].length === 3) {
    colorString = [...colorString].map(char => char + char).join('')
  }

  const integer = parseInt(colorString, 16)
  const r = (integer >> 16) & 0xFF
  const g = (integer >> 8) & 0xFF
  const b = integer & 0xFF

  return [r, g, b]
}

/**
 * Converts RGB to keyword name
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {Keyword} CSS keyword name or closest match
 */
export function rgbToKeyword(rgb: RGB): Keyword {
  const key = rgb.join(',')
  const reversed = REVERSE_KEYWORDS[key]
  if (reversed) return reversed

  let currentClosestDistance = Number.POSITIVE_INFINITY
  let currentClosestKeyword: Keyword | null = null

  for (const [keyword, value] of Object.entries(CSS_KEYWORDS)) {
    const distance = comparativeDistance(rgb, value)

    // Check if its less, if so set as closest
    if (distance < currentClosestDistance) {
      currentClosestDistance = distance
      currentClosestKeyword = keyword
    }
  }

  return currentClosestKeyword ?? 'black' as Keyword
}

/**
 * Converts CSS keyword to RGB
 * @param {Keyword} keyword - CSS keyword name
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function keywordToRgb(keyword: Keyword): RGB {
  const rgb = CSS_KEYWORDS[keyword.toLowerCase()]
  return rgb ?? [0, 0, 0]
}

/**
 * Converts RGB to ANSI 16-color code
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @param {number | null} saturation - Optional saturation override
 * @returns {ANSI16} ANSI 16-color code [0-15]
 */
export function rgbToAnsi16(rgb: RGB, saturation: number | null = null): ANSI16 {
  const [r, g, b] = rgb
  let value = saturation ?? rgbToHsv(rgb)[2]

  value = Math.round(value / 50)

  if (value === 0) {
    return 30 as ANSI16
  }

  let ansi = 30
    + ((Math.round(b / 255) << 2)
    | (Math.round(g / 255) << 1)
    | Math.round(r / 255))

  if (value === 2) {
    ansi += 60
  }

  return ansi
}

/**
 * Converts ANSI 16-color code to RGB
 * @param {ANSI16} ansi16 - ANSI 16-color code [0-15]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function ansi16ToRgb(ansi16: ANSI16): RGB {
  let color = ansi16 % 10

  // Handle greyscale
  if (color === 0 || color === 7) {
    if (ansi16 > 50) {
      color += 3.5
    }

    color = color / 10.5 * 255

    return [color, color, color] as RGB
  }

  const mult = (Math.trunc(ansi16 > 50 ? 1 : 0) + 1) * 0.5
  const r = ((color & 1) * mult) * 255
  const g = (((color >> 1) & 1) * mult) * 255
  const b = (((color >> 2) & 1) * mult) * 255

  return [r, g, b] as RGB
}

/**
 * Converts RGB to ANSI 256-color code
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {ANSI256} ANSI 256-color code [0-255]
 */
export function rgbToAnsi256(rgb: RGB): ANSI256 {
  const r = rgb[0]
  const g = rgb[1]
  const b = rgb[2]

  // We use the extended greyscale palette here, with the exception of
  // black and white. normal palette only has 4 greyscale shades.
  if (r >> 4 === g >> 4 && g >> 4 === b >> 4) {
    if (r < 8) {
      return 16 as ANSI256
    }

    if (r > 248) {
      return 231 as ANSI256
    }

    return (Math.round(((r - 8) / 247) * 24) + 232)
  }

  const ansi = 16
    + (36 * Math.round(r / 255 * 5))
    + (6 * Math.round(g / 255 * 5))
    + Math.round(b / 255 * 5)

  return ansi
}

/**
 * Converts ANSI 256-color code to RGB
 * @param {ANSI256} ansi256 - ANSI 256-color code [0-255]
 * @returns {RGB} RGB color values [0-255, 0-255, 0-255]
 */
export function ansi256ToRgb(ansi256: ANSI256): RGB {
  // Handle greyscale
  if (ansi256 >= 232) {
    const c = (ansi256 - 232) * 10 + 8
    return [c, c, c] as RGB
  }

  ansi256 -= 16

  let rem: number
  const r = Math.floor(ansi256 / 36) / 5 * 255
  const g = Math.floor((rem = ansi256 % 36) / 6) / 5 * 255
  const b = (rem % 6) / 5 * 255

  return [r, g, b] as RGB
}

/**
 * Parses a color string and returns RGB values with comprehensive format support
 * @param {string} colorString - Color string to parse (hex, rgb, rgba, hsl, hsla, or CSS keyword)
 * @returns {RGB | null} RGB color values or null if invalid
 * @example
 * // Parse various color formats
 * parseColorString('#ff0000') // [255, 0, 0]
 * parseColorString('rgb(255, 0, 0)') // [255, 0, 0]
 * parseColorString('hsl(0, 100%, 50%)') // [255, 0, 0]
 * parseColorString('red') // [255, 0, 0]
 */
export function parseColorString(colorString: string): RGB | null {
  if (!colorString || typeof colorString !== 'string') {
    return null
  }

  const trimmed = colorString.trim().toLowerCase()

  // Handle hex format with proper validation
  if (trimmed.startsWith('#')) {
    return hexToRgb(trimmed)
  }

  // Check CSS keywords first for performance
  if (CSS_KEYWORDS[trimmed]) {
    return CSS_KEYWORDS[trimmed]
  }

  // Handle hex without # prefix
  const hexMatch = /^([a-f\d]{3}|[a-f\d]{6})$/i.exec(trimmed)
  if (hexMatch?.[1]) {
    return hexToRgb('#' + hexMatch[1])
  }

  // Handle rgb/rgba with comprehensive pattern matching
  const rgbMatch = /rgba?\(\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*(?:,\s*([+-]?[\d.]+))?\s*\)/.exec(trimmed)
  if (rgbMatch?.[1] && rgbMatch?.[2] && rgbMatch?.[3]) {
    return [
      clamp(Math.round(parseFloat(rgbMatch[1])), 0, 255),
      clamp(Math.round(parseFloat(rgbMatch[2])), 0, 255),
      clamp(Math.round(parseFloat(rgbMatch[3])), 0, 255)
    ]
  }

  // Handle hsl/hsla with proper validation
  const hslMatch = /hsla?\(\s*([+-]?[\d.]+)\s*,\s*([+-]?[\d.]+)%\s*,\s*([+-]?[\d.]+)%\s*(?:,\s*([+-]?[\d.]+))?\s*\)/.exec(trimmed)
  if (hslMatch?.[1] && hslMatch?.[2] && hslMatch?.[3]) {
    const h = parseFloat(hslMatch[1])
    const s = clamp(parseFloat(hslMatch[2]), 0, 100)
    const l = clamp(parseFloat(hslMatch[3]), 0, 100)
    return hslToRgb([h, s, l])
  }

  return null
}

/**
 * Adjusts the opacity/alpha of a color
 * @param hsl - HSL color values
 * @param opacity - New opacity value [0-1]
 * @returns HSLA color with adjusted opacity
 */
export function adjustOpacity(hsl: HSL, opacity: number): readonly [number, number, number, number] {
  const [h, s, l] = hsl
  const clampedOpacity = clamp(opacity, 0, 1)
  return [h, s, l, clampedOpacity]
}

/**
 * Lightens an HSL color by increasing its lightness value
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} amount - Amount to lighten [0-1], where 0.1 = 10% lighter
 * @returns {HSL} Lightened HSL color
 * @example
 * // Lighten a color by 20%
 * lighten([200, 50, 50], 0.2) // [200, 50, 60]
 */
export function lighten(hsl: HSL, amount: number): HSL {
  const [h, s, l] = hsl
  const newL = clamp(l + (amount * 100), 0, 100)
  return [h, s, newL]
}

/**
 * Darkens an HSL color by a given ratio
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} amount - Amount to darken [0-1], where 0.1 = 10% darker
 * @returns {HSL} Darkened HSL color
 * @example
 * // Darken a color by 20%
 * darken([200, 50, 50], 0.2) // [200, 50, 30]
 */
export function darken(hsl: HSL, amount: number): HSL {
  const [h, s, l] = hsl
  const newL = clamp(l - (amount * 100), 0, 100)
  return [h, s, newL]
}

/**
 * Increases the saturation of an HSL color
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} amount - Amount to saturate [0-1], where 0.1 = 10% more saturated
 * @returns {HSL} Saturated HSL color
 */
export function saturate(hsl: HSL, amount: number): HSL {
  const [h, s, l] = hsl
  const newS = clamp(s + (amount * 100), 0, 100)
  return [h, newS, l]
}

/**
 * Decreases the saturation of an HSL color
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} amount - Amount to desaturate [0-1], where 0.1 = 10% less saturated
 * @returns {HSL} Desaturated HSL color
 */
export function desaturate(hsl: HSL, amount: number): HSL {
  const [h, s, l] = hsl
  const newS = clamp(s - (amount * 100), 0, 100)
  return [h, newS, l]
}

/**
 * Rotates the hue of an HSL color by a specified number of degrees
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} degrees - Degrees to rotate hue (-360 to 360)
 * @returns {HSL} HSL color with rotated hue
 * @example
 * // Rotate hue by 180 degrees (complementary color)
 * rotateHue([180, 50, 50], 180) // [0, 50, 50]
 */
export function rotateHue(hsl: HSL, degrees: number): HSL {
  const [h, s, l] = hsl
  const newH = normalizeHue(h + degrees)
  return [newH, s, l]
}

/**
 * Mixes two RGB colors using linear interpolation
 * @param {RGB} rgb1 - First RGB color
 * @param {RGB} rgb2 - Second RGB color
 * @param {number} weight - Mix weight [0-1], where 0.5 is equal mix, 0 is all rgb1, 1 is all rgb2
 * @returns {RGB} Mixed RGB color
 * @example
 * // Mix red and blue equally
 * mixColors([255, 0, 0], [0, 0, 255], 0.5) // [128, 0, 128]
 */
export function mixColors(rgb1: RGB, rgb2: RGB, weight = 0.5): RGB {
  const clampedWeight = clamp(weight, 0, 1)
  const inverseWeight = 1 - clampedWeight

  return [
    Math.round(rgb1[0] * inverseWeight + rgb2[0] * clampedWeight),
    Math.round(rgb1[1] * inverseWeight + rgb2[1] * clampedWeight),
    Math.round(rgb1[2] * inverseWeight + rgb2[2] * clampedWeight)
  ]
}

/**
 * Formats RGB color as CSS string with optional alpha
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @param {number} alpha - Alpha value [0-1], optional
 * @returns {string} CSS color string
 */
export function formatRgbString(rgb: RGB, alpha?: number): string {
  const [r, g, b] = [
    Math.round(clamp(rgb[0], 0, 255)),
    Math.round(clamp(rgb[1], 0, 255)),
    Math.round(clamp(rgb[2], 0, 255))
  ]

  if (alpha !== undefined && alpha < 1) {
    const a = clamp(alpha, 0, 1)
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Formats RGB color as CSS percentage string with optional alpha
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @param {number} alpha - Alpha value [0-1], optional
 * @returns {string} CSS color string with percentages
 */
export function formatRgbPercentString(rgb: RGB, alpha?: number): string {
  const r = Math.round(clamp(rgb[0] / 255 * 100, 0, 100))
  const g = Math.round(clamp(rgb[1] / 255 * 100, 0, 100))
  const b = Math.round(clamp(rgb[2] / 255 * 100, 0, 100))

  if (alpha !== undefined && alpha < 1) {
    const a = clamp(alpha, 0, 1)
    return `rgba(${r}%, ${g}%, ${b}%, ${a})`
  }
  
  return `rgb(${r}%, ${g}%, ${b}%)`
}

/**
 * Formats HSL color as CSS string with optional alpha
 * @param {HSL} hsl - HSL color values [0-360, 0-100, 0-100]
 * @param {number} alpha - Alpha value [0-1], optional
 * @returns {string} CSS color string
 */
export function formatHslString(hsl: HSL, alpha?: number): string {
  const [h, s, l] = [
    Math.round(hsl[0]),
    Math.round(clamp(hsl[1], 0, 100)),
    Math.round(clamp(hsl[2], 0, 100))
  ]
  
  if (alpha !== undefined && alpha < 1) {
    return `hsla(${h}, ${s}%, ${l}%, ${alpha})`
  }
  
  return `hsl(${h}, ${s}%, ${l}%)`
}

/**
 * Formats HWB color as CSS string
 * @param {HWB} hwb - HWB color values [0-360, 0-100, 0-100]
 * @param {number} alpha - Alpha value [0-1], optional
 * @returns {string} CSS color string
 */
export function formatHwbString(hwb: HWB, alpha?: number): string {
  const [h, w, b] = [
    Math.round(hwb[0]),
    Math.round(clamp(hwb[1], 0, 100)),
    Math.round(clamp(hwb[2], 0, 100))
  ]
  
  let a = ''
  if (alpha !== undefined && alpha < 1) {
    a = `, ${alpha}`
  }

  return `hwb(${h}, ${w}%, ${b}%${a})`
}

/**
 * Universal color converter that can convert between any supported color spaces
 * @param {ColorValue} color - Input color in any supported format
 * @param {ColorSpace} fromSpace - Source color space
 * @param {ColorSpace} toSpace - Target color space
 * @returns {ColorValue} Converted color value
 */
export function convertColor(color: ColorValue, fromSpace: ColorSpace, toSpace: ColorSpace): ColorValue {
  if (fromSpace === toSpace) return color

  // Convert to RGB as intermediate format
  let rgb: RGB
  let alpha: number | undefined

  switch (fromSpace) {
    case 'rgb':
      rgb = color as RGB
      break
    case 'rgba': {
      const [r, g, b, a] = color as RGBA
      rgb = [r, g, b] as RGB
      alpha = a
      break
    }
    case 'hsl':
      rgb = hslToRgb(color as HSL)
      break
    case 'hsla': {
      const [h, s, l, a] = color as HSLA
      rgb = hslToRgb([h, s, l] as HSL)
      alpha = a
      break
    }
    case 'hsv':
      rgb = hsvToRgb(color as HSV)
      break
    case 'hsva': {
      const [h, s, v, a] = color as HSVA
      rgb = hsvToRgb([h, s, v] as HSV)
      alpha = a
      break
    }
    case 'hwb':
      rgb = hwbToRgb(color as HWB)
      break
    case 'hwba': {
      const [h, w, b, a] = color as HWBA
      rgb = hwbToRgb([h, w, b] as HWB)
      alpha = a
      break
    }
    case 'cmyk':
      rgb = cmykToRgb(color as CMYK)
      break
    case 'xyz':
      rgb = xyzToRgb(color as XYZ)
      break
    case 'lab':
      rgb = xyzToRgb(labToXyz(color as LAB))
      break
    case 'oklab':
      rgb = oklabToRgb(color as OKLAB)
      break
    case 'lch':
      rgb = xyzToRgb(labToXyz(lchToLab(color as LCH)))
      break
    case 'oklch':
      rgb = oklabToRgb(oklchToOklab(color as OKLCH))
      break
    case 'hcg':
      rgb = hcgToRgb(color as HCG)
      break
    case 'apple':
      rgb = appleToRgb(color as Apple)
      break
    case 'gray':
      rgb = grayscaleToRgb(color as Gray)
      break
    case 'hex':
      rgb = hexToRgb(color as HEX)
      break
    case 'keyword':
      rgb = keywordToRgb(color as Keyword)
      break
    case 'ansi16':
      rgb = ansi16ToRgb(color as ANSI16)
      break
    case 'ansi256':
      rgb = ansi256ToRgb(color as ANSI256)
      break
    default:
      throw new Error(`Unsupported source color space: ${fromSpace as string}`)
  }

  // Convert from RGB to target format
  switch (toSpace) {
    case 'rgb':
      return rgb
    case 'rgba': {
      const [r, g, b] = rgb
      return [r, g, b, alpha ?? 1] as RGBA
    }
    case 'hsl':
      return rgbToHsl(rgb)
    case 'hsla': {
      const [h, s, l] = rgbToHsl(rgb)
      return [h, s, l, alpha ?? 1] as HSLA
    }
    case 'hsv':
      return rgbToHsv(rgb)
    case 'hsva': {
      const [h, s, v] = rgbToHsv(rgb)
      return [h, s, v, alpha ?? 1] as HSVA
    }
    case 'hwb':
      return rgbToHwb(rgb)
    case 'hwba': {
      const [h, w, b] = rgbToHwb(rgb)
      return [h, w, b, alpha ?? 1] as HWBA
    }
    case 'cmyk':
      return rgbToCmyk(rgb)
    case 'xyz':
      return rgbToXyz(rgb)
    case 'lab':
      return rgbToLab(rgb)
    case 'oklab':
      return rgbToOklab(rgb)
    case 'lch':
      return labToLch(rgbToLab(rgb))
    case 'oklch':
      return oklabToOklch(rgbToOklab(rgb))
    case 'hcg':
      return rgbToHcg(rgb)
    case 'apple':
      return rgbToApple(rgb)
    case 'gray':
      return rgbToGrayscale(rgb)
    case 'hex':
      return rgbToHex(rgb)
    case 'keyword':
      return rgbToKeyword(rgb)
    case 'ansi16':
      return rgbToAnsi16(rgb)
    case 'ansi256':
      return rgbToAnsi256(rgb)
    default:
      throw new Error(`Unsupported target color space: ${toSpace as string}`)
  }
}
